import { Team, TowerLevel } from "../types";

/**
 * Organizes teams into tower levels based on ranking.
 *
 * Level 1 holds `topRowTeamCount` teams; every level below holds `teamsPerRow`.
 *
 * @param teams - Array of teams to organize
 * @param teamsPerRow - Number of teams per row for non-top levels
 * @param topRowTeamCount - Number of teams on the top row (1 or 2)
 */
export function organizeTowerLevels(
  teams: Team[],
  teamsPerRow: number = 5,
  topRowTeamCount: number = 1,
): TowerLevel[] {
  const sortedTeams = [...teams].sort((a, b) => a.ranking - b.ranking);
  const levels: TowerLevel[] = [];

  if (sortedTeams.length === 0) return levels;

  let currentIndex = 0;

  const level1Teams = sortedTeams.slice(0, topRowTeamCount);
  if (level1Teams.length > 0) {
    levels.push({
      level: 1,
      teams: level1Teams,
      maxTeams: topRowTeamCount,
    });
    currentIndex += level1Teams.length;
  }

  let levelNumber = 2;
  while (currentIndex < sortedTeams.length) {
    const levelTeams = sortedTeams.slice(
      currentIndex,
      currentIndex + teamsPerRow,
    );
    levels.push({
      level: levelNumber,
      teams: levelTeams,
      maxTeams: teamsPerRow,
    });
    currentIndex += teamsPerRow;
    levelNumber++;
  }

  return levels;
}

/**
 * Get the tower level for a team based on its position in the sorted array.
 *
 * Level 1 covers positions 1..topRowTeamCount; subsequent levels each cover
 * `teamsPerRow` positions.
 *
 * @param position - Team's position in sorted array (1-based, where 1 is the best team)
 * @param teamsPerRow - Number of teams per row for non-top levels
 * @param topRowTeamCount - Number of teams on the top row (1 or 2)
 */
export function getTowerLevel(
  position: number,
  teamsPerRow: number = 5,
  topRowTeamCount: number = 1,
): number {
  if (position <= topRowTeamCount) {
    return 1;
  }

  const remainingPosition = position - topRowTeamCount;
  return Math.floor((remainingPosition - 1) / teamsPerRow) + 2;
}
